// src/components/JourneyTimeline.jsx — Light Theme
import React from "react";
import { Tooltip } from "antd";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { useScheduleForCohort } from "../context/MasterDataContext";

dayjs.extend(isSameOrAfter);

const STATUS_LIGHT = {
  Pending:   { color:"#b45309", bg:"#fff7ed", border:"#fed7aa" },
  Completed: { color:"#15803d", bg:"#f0fdf4", border:"#bbf7d0" },
  Missed:    { color:"#b91c1c", bg:"#fef2f2", border:"#fecaca" },
  Upcoming:  { color:"#93b4d0", bg:"#f0f7ff", border:"#bfdbfe" },
};

// ── Auto-calculate status when no call is logged ────────────────
// dueDate = dateOfJoining + call.day
// today > dueDate + 2 grace  →  Missed
// today >= dueDate            →  Pending
// today <  dueDate            →  Upcoming
function computeAutoStatus(dateOfJoining, callDay) {
  if (!dateOfJoining || callDay == null) return "Upcoming";
  const dueDate   = dayjs(dateOfJoining).add(callDay, "day");
  const today     = dayjs().startOf("day");
  const graceDays = 2;
  if (today.isAfter(dueDate.add(graceDays, "day"))) return "Missed";
  if (today.isSameOrAfter(dueDate))                 return "Pending";
  return "Upcoming";
}

function getCallStatus(employee, callKey, callSchedule) {
  const calls = employee?.calls;
  if (!calls) {
    const sched = callSchedule?.find(s => s.key === callKey);
    return computeAutoStatus(employee?.dateOfJoining, sched?.day);
  }
  const call = Array.isArray(calls)
    ? calls.find(c => c.callKey === callKey)
    : calls[callKey];

  // Logged in DB → use saved status
  if (call?.status) return call.status;

  // Not logged → auto-calculate from DOJ + schedule day
  const sched = callSchedule?.find(s => s.key === callKey);
  return computeAutoStatus(employee?.dateOfJoining, sched?.day);
}

function getCallData(employee, callKey) {
  const calls = employee?.calls;
  if (!calls) return null;
  return Array.isArray(calls)
    ? (calls.find(c => c.callKey === callKey) ?? null)
    : (calls[callKey] ?? null);
}

// ── Exports ─────────────────────────────────────────────────────
export default function JourneyTimeline({ employee, mini = false }) {
  if (!employee) return null;
  if (mini) return <MiniTimeline employee={employee} />;
  return <FullTimeline employee={employee} />;
}

function MiniTimeline({ employee }) {
  const callSchedule = useScheduleForCohort(employee?.cohortName);
  return (
    <div style={{ display:"flex", alignItems:"center", gap:4 }}>
      {callSchedule.map(({ key, label }) => {
        const status = getCallStatus(employee, key, callSchedule);
        const cfg    = STATUS_LIGHT[status] || {};
        return (
          <Tooltip key={key} title={`${label}: ${status}`} placement="top" color="blue">
            <span style={{
              display:"inline-block", width:10, height:10, borderRadius:"50%",
              background: status === "Completed" ? cfg.color : cfg.bg,
              border:`1.5px solid ${cfg.border || cfg.color}`,
              cursor:"default", flexShrink:0,
            }} />
          </Tooltip>
        );
      })}
    </div>
  );
}

function FullTimeline({ employee }) {
  const callSchedule = useScheduleForCohort(employee?.cohortName);
  return (
    <div style={{ display:"flex", alignItems:"flex-start", padding:"20px 0 8px", overflowX:"auto", gap:0 }}>
      {callSchedule.map(({ key, label, day }, index) => {
        const status     = getCallStatus(employee, key, callSchedule);
        const cfg        = STATUS_LIGHT[status] || {};
        const isLast     = index === callSchedule.length - 1;
        const callData   = getCallData(employee, key);
        const isComplete = status === "Completed";
        const isMissed   = status === "Missed";
        const isPending  = status === "Pending";

        const dueDate = employee.dateOfJoining
          ? dayjs(employee.dateOfJoining).add(day, "day").format("DD MMM YY")
          : null;

        return (
          <div key={key} style={{ display:"flex", alignItems:"flex-start", flex:isLast?"0 0 auto":"1 1 0", minWidth:80 }}>
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:8 }}>
              <Tooltip title={
                <div>
                  <div style={{ fontWeight:700 }}>{label} Call</div>
                  <div style={{ color:"#fcd34d" }}>Status: {status}</div>
                  {callData?.callDate
                    ? <div>Logged: {callData.callDate}</div>
                    : dueDate && <div>Due: {dueDate}</div>
                  }
                  {callData?.remarks && <div style={{ marginTop:4, maxWidth:180 }}>{callData.remarks}</div>}
                </div>
              } placement="top">
                <div style={{
                  width:36, height:36, borderRadius:"50%",
                  background: cfg.bg,
                  border:`2px solid ${cfg.color || "#c5d6e8"}`,
                  display:"flex", alignItems:"center", justifyContent:"center",
                  cursor:"default", transition:"all 0.2s",
                  boxShadow: isPending ? `0 0 8px ${cfg.color}40` : "none",
                  position:"relative",
                }}>
                  {isComplete && <CheckOutlined style={{ color:cfg.color, fontSize:14 }} />}
                  {isMissed   && <CloseOutlined style={{ color:cfg.color, fontSize:12 }} />}
                  {isPending  && <span style={{ fontSize:9, fontWeight:800, color:cfg.color }}>DUE</span>}
                  {status === "Upcoming" && <span style={{ fontSize:8, fontWeight:600, color:cfg.color, textAlign:"center" }}>D{day}</span>}
                  {isPending && (
                    <span style={{
                      position:"absolute", inset:-4, borderRadius:"50%",
                      border:`2px solid ${cfg.color}`, animation:"tlPulse 1.8s infinite", pointerEvents:"none",
                    }} />
                  )}
                </div>
              </Tooltip>
              <div style={{ textAlign:"center", minWidth:60 }}>
                <div style={{ fontSize:11, fontWeight:600, color:cfg.color || "#7a96b0" }}>{label}</div>
                <div style={{ fontSize:10, color:"#7a96b0", marginTop:2 }}>
                  {callData?.callDate || (dueDate ? `Due ${dueDate}` : status)}
                </div>
              </div>
            </div>
            {!isLast && (
              <div style={{
                flex:1, height:2, marginTop:17,
                background: isComplete ? cfg.color : "#eaf1f8",
                transition:"background 0.3s",
              }} />
            )}
          </div>
        );
      })}
      <style>{`@keyframes tlPulse{0%,100%{opacity:0.8;transform:scale(1)}50%{opacity:0;transform:scale(1.5)}}`}</style>
    </div>
  );
}